import { getRequestPAdmin } from "common/HttpClient/http-utility";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  CUSTOMER_ACCESS_STATUS,
  isCustomerAccessStatus,
  isRevokedCustomerAccessStatus,
} from "common/Constants/AccessStatus.constants";
import {
  NO_CUSTOMER_ACCESS_ERROR_MESSAGE,
  SESSION_ACCOUNT_SUSPENDED_MESSAGE,
  SESSION_CUSTOMER_ACCESS_LOAD_ERROR_MESSAGE,
  SESSION_ME_LOAD_ERROR_MESSAGE,
  buildActiveCustomerAccessRevokedMessage,
  buildActiveCustomerUnavailableMessage,
  SESSION_ACTIVE_CUSTOMER_ACCESS_REVOKED_TITLE,
  SESSION_ACTIVE_CUSTOMER_UNAVAILABLE_TITLE,
} from "common/Constants/SessionMe.constants";
import {
  applyCustomerContextHeaders,
  clearCustomerContextHeaders,
} from "common/HttpClient/CustomerContextHeaders";
import { fetchAppActiveCustomers } from "common/Services/ActiveCustomersMaster";
import { fetchCustomerRolesData, syncCustomerRoleOptions } from "common/Services/CustomerRolesMaster";
import {
  isNonePlatformRole,
  isPlatformAdminProfile,
} from "common/Utilities/Role.utils";
import { getAccessibleRouteValuesFromEffectiveAccess } from "common/Utilities/EffectiveAccess.utils";
import { putMeActiveCustomerRequest } from "common/Services/MeSession.services";
import {
  buildStoredCustomerInfo,
  clearStoredSelectedCustomer,
  hasStoredSelectedCustomer,
  isPlatformAdminHomePath,
  parseStoredSelectedCustomer,
  resetPlatformAdminCustomerSelection,
  resolveStoredCustomerFromAccessList,
  writeStoredSelectedCustomer,
} from "common/Utilities/CustomerSelection.utils";

export const formatUserDisplayName = ({ firstName, lastName } = {}) =>
  [firstName, lastName]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");

const extractEnvelope = (data) => {
  if (data == null) {
    return null;
  }
  if (typeof data === "object" && "response" in data && data.response !== undefined) {
    return data.response;
  }
  return data;
};

const mapMeCustomersToAccess = (customers) =>
  (customers ?? []).map((c) => {
    return {
      id: c.customerId,
      name: c.customerName,
      customerCode: c.customerCode,
      customerId: c.customerId,
      roles: c.roles ?? [],
      accessStatus: c.accessStatus,
      canSwitch: c.canSwitch,
      timeZone: c.timeZone ?? c.timezone,
    };
  });

const mergeMeIntoProfile = (profile, me) => {
  if (!me || typeof me !== "object") {
    return;
  }
  profile.meUserId = me.id ?? profile.meUserId;
  if (me.username != null && String(me.username).trim() !== "") {
    profile.userid = String(me.username).trim();
  }
  if (me.email) {
    profile.email = me.email;
  }
  profile.firstName = me.firstName ?? profile.firstName;
  profile.lastName = me.lastName ?? profile.lastName;
  profile.name = formatUserDisplayName(me);
  profile.platformRole = me.platformRole;
  profile.accountStatus = me.accountStatus;
  profile.activeCustomerId = me.activeCustomerId;
  profile.activeCustomerCode = me.activeCustomerCode ?? null;
  profile.activeCustomerName = me.activeCustomerName;
  profile.activeCustomerRoles = me.activeCustomerRoles;
  profile.sessionEpoch = me.sessionEpoch;
  profile.claimVersion = me.claimVersion;
  profile.sessionFromMe = true;
  profile.customerId = me.activeCustomerId;
  profile.customerName = me.activeCustomerName;
  profile.customerRoles = me.activeCustomerRoles;
  profile.customerCode = me.activeCustomerCode ?? profile.customerCode;
};

const applyDefaultCustomerSelection = (rows, profile, me) => {
  if (!rows?.length) {
    return;
  }
  const resolved = resolveStoredCustomerFromAccessList(rows, me);
  const customerInfo = resolved ?? buildStoredCustomerInfo(null, rows[0], me);
  writeStoredSelectedCustomer(customerInfo, profile?.userid);
};

export const hasActiveCustomerOnMe = (me) => {
  const customerId = me?.activeCustomerId;
  const customerCode = me?.activeCustomerCode;
  return (
    customerId != null &&
    String(customerId).trim() !== "" &&
    customerCode != null &&
    String(customerCode).trim() !== ""
  );
};

export const findCustomerAccessRowForMe = (me, rows = []) => {
  if (!hasActiveCustomerOnMe(me) || !rows?.length) {
    return null;
  }
  return (
    rows.find(
      (row) =>
        String(row.id ?? row.customerId) === String(me.activeCustomerId) ||
        String(row.customerCode) === String(me.activeCustomerCode)
    ) ?? null
  );
};

const isActiveCustomerRowUnusable = (row) =>
  !row ||
  isRevokedCustomerAccessStatus(row.accessStatus) ||
  row.canSwitch === false;

export const hasUsableActiveCustomerAccessOnMe = (me, rows = []) => {
  const activeRow = findCustomerAccessRowForMe(me, rows);
  return activeRow != null && !isActiveCustomerRowUnusable(activeRow);
};

export const getActiveCustomerAccessRevokedWarning = (me, rows = []) => {
  const activeRow = findCustomerAccessRowForMe(me, rows);
  if (!activeRow || !isRevokedCustomerAccessStatus(activeRow.accessStatus)) {
    return null;
  }
  const customerName =
    activeRow.name ??
    activeRow.customerName ??
    me?.activeCustomerName ??
    "";
  return buildActiveCustomerAccessRevokedMessage(customerName);
};

export const getActiveCustomerUnavailableWarning = (me, rows = []) => {
  const activeRow = findCustomerAccessRowForMe(me, rows);
  if (!activeRow || activeRow.canSwitch !== false) {
    return null;
  }
  if (isRevokedCustomerAccessStatus(activeRow.accessStatus)) {
    return null;
  }
  const customerName =
    activeRow.name ??
    activeRow.customerName ??
    me?.activeCustomerName ??
    "";
  return buildActiveCustomerUnavailableMessage(customerName);
};

const clearEffectiveAccessFromProfile = (profile) => {
  profile.effectiveAccess = null;
  profile.accessTo = [];
};

const syncEffectiveAccessWhenAllowed = async (profile, me, rows) => {
  if (hasUsableActiveCustomerAccessOnMe(me, rows)) {
    return syncEffectiveAccessFromMe(profile, me);
  }

  clearEffectiveAccessFromProfile(profile);
  return null;
};

const shouldApplyActiveCustomerSessionWarning = (profile) =>
  !(isPlatformAdminProfile(profile) && !hasStoredSelectedCustomer());

const applyActiveCustomerSessionWarning = (profile, me, rows) => {
  if (!shouldApplyActiveCustomerSessionWarning(profile)) {
    delete profile.sessionWarning;
    delete profile.sessionWarningTitle;
    return;
  }

  const revokedWarning = getActiveCustomerAccessRevokedWarning(me, rows);
  if (revokedWarning) {
    profile.sessionWarning = revokedWarning;
    profile.sessionWarningTitle = SESSION_ACTIVE_CUSTOMER_ACCESS_REVOKED_TITLE;
    return;
  }

  const unavailableWarning = getActiveCustomerUnavailableWarning(me, rows);
  if (unavailableWarning) {
    profile.sessionWarning = unavailableWarning;
    profile.sessionWarningTitle = SESSION_ACTIVE_CUSTOMER_UNAVAILABLE_TITLE;
    return;
  }

  delete profile.sessionWarning;
  delete profile.sessionWarningTitle;
};

const shouldBootstrapActiveCustomerForNone = (me) =>
  isNonePlatformRole(me) && !hasActiveCustomerOnMe(me);

const findBootstrapActiveCustomer = (rows = []) =>
  rows.find(
    (row) =>
      String(row.accessStatus ?? "").trim() === CUSTOMER_ACCESS_STATUS.ACTIVE
  ) ?? null;

const bootstrapActiveCustomerFromAccess = async (profile, me, rows) => {
  if (!rows?.length) {
    return { me, error: NO_CUSTOMER_ACCESS_ERROR_MESSAGE };
  }

  const bootstrapCustomer = findBootstrapActiveCustomer(rows);
  if (!bootstrapCustomer) {
    return { me, error: NO_CUSTOMER_ACCESS_ERROR_MESSAGE };
  }

  const putResult = await putMeActiveCustomerRequest(bootstrapCustomer);
  if (putResult?.error) {
    applyDefaultCustomerSelection(rows, profile, me);
    return { me };
  }

  const refreshedMe = await fetchMePayload();
  if (!refreshedMe || typeof refreshedMe !== "object") {
    applyDefaultCustomerSelection(rows, profile, me);
    return { me };
  }

  mergeMeIntoProfile(profile, refreshedMe);
  syncActiveCustomerStorageFromMe(profile, refreshedMe);
  await syncEffectiveAccessFromMe(profile, refreshedMe);
  return { me: refreshedMe };
};

const clearActiveCustomerSelection = () => {
  clearStoredSelectedCustomer({ dispatchEvents: true });
  clearCustomerContextHeaders();
};

export const fetchMePayload = async () => {
  const path = INFRAMARK_CONSTANTS.login.services.me;
  const response = await getRequestPAdmin(path);
  if (response?.error) {
    return null;
  }
  return extractEnvelope(response?.data);
};

const resolveEffectiveAccessCustomerIdParam = (customerId) => {
  if (customerId == null || String(customerId).trim() === "") {
    return null;
  }
  const numeric = Number(customerId);
  return Number.isFinite(numeric) ? numeric : customerId;
};

export const fetchMeEffectiveAccess = async (customerId) => {
  const path = INFRAMARK_CONSTANTS.login.services.meEffectiveAccess;
  const resolvedId = resolveEffectiveAccessCustomerIdParam(customerId);
  const params =
    resolvedId != null ? { customerId: resolvedId } : undefined;
  const response = await getRequestPAdmin(path, params);
  if (response?.error) {
    return null;
  }
  return extractEnvelope(response?.data);
};

export const syncEffectiveAccessFromMe = async (profile, me) => {
  const customerId = me?.activeCustomerId;
  const effectiveAccess = await fetchMeEffectiveAccess(customerId);
  profile.effectiveAccess = effectiveAccess ?? null;
  if (effectiveAccess) {
    profile.accessTo = getAccessibleRouteValuesFromEffectiveAccess(effectiveAccess);
  }
  return profile.effectiveAccess;
};

export const syncActiveCustomerStorageFromMe = (profile, me) => {
  const customerId = me?.activeCustomerId;
  const customerCode = me?.activeCustomerCode;
  if (
    customerId == null ||
    String(customerId).trim() === "" ||
    !customerCode ||
    String(customerCode).trim() === ""
  ) {
    return null;
  }

  const accessRows = profile?.customerAccess ?? [];
  const matchedRow = accessRows.find(
    (row) =>
      String(row.id ?? row.customerId) === String(customerId) ||
      String(row.customerCode) === String(customerCode)
  );

  const customerInfo = buildStoredCustomerInfo(null, matchedRow, me);

  writeStoredSelectedCustomer(customerInfo, profile?.userid);
  return customerInfo;
};

export const refreshUserProfileFromMe = async (profile, loadUserInformation) => {
  const me = await fetchMePayload();
  if (!me || typeof me !== "object") {
    return { error: SESSION_ME_LOAD_ERROR_MESSAGE };
  }

  mergeMeIntoProfile(profile, me);
  const rows = profile.customerAccess ?? [];
  let customerInfo = null;
  if (hasActiveCustomerOnMe(me)) {
    if (hasUsableActiveCustomerAccessOnMe(me, rows)) {
      customerInfo = syncActiveCustomerStorageFromMe(profile, me);
    } else {
      clearActiveCustomerSelection();
    }
  }
  applyActiveCustomerSessionWarning(profile, me, rows);
  const effectiveAccess = await syncEffectiveAccessWhenAllowed(profile, me, rows);
  await loadUserInformation(profile);
  return { me, customerInfo, effectiveAccess };
};

const fetchCustomerAccessPayload = async () => {
  const path = INFRAMARK_CONSTANTS.login.services.meCustomerAccess;
  const response = await getRequestPAdmin(path);
  if (response?.error) {
    return null;
  }
  return extractEnvelope(response?.data);
};

export async function fetchMeCustomerAccessRows() {
  let accessBody;
  try {
    accessBody = await fetchCustomerAccessPayload();
  } catch {
    accessBody = null;
  }
  if (!accessBody || typeof accessBody !== "object") {
    return null;
  }
  const customersRaw = Array.isArray(accessBody?.customers)
    ? accessBody.customers
    : [];
  return mapMeCustomersToAccess(customersRaw);
}

export async function syncProfileCustomerAccessSession(
  profile,
  rows,
  loadUserInformation
) {
  if (!profile?.sessionFromMe || !rows) {
    return;
  }
  profile.customerAccess = rows;
  const me = profile;
  if (hasActiveCustomerOnMe(me) && !hasUsableActiveCustomerAccessOnMe(me, rows)) {
    clearActiveCustomerSelection();
    clearEffectiveAccessFromProfile(profile);
  }
  applyActiveCustomerSessionWarning(profile, me, rows);
  await loadUserInformation?.(profile);
}

export async function initializeUserSessionFromMe(profile, {
  loadUserInformation,
  setCustomersList,
  setCustomerRoleOptions,
  setCustomerRoleLabelMap,
  setActiveCustomersList,
}) {
  clearCustomerContextHeaders();

  let me = await fetchMePayload();
  if (!me || typeof me !== "object") {
    return SESSION_ME_LOAD_ERROR_MESSAGE;
  }

  mergeMeIntoProfile(profile, me);

  const isNonePlatform = isNonePlatformRole(me);
  const isPlatformAdminFromMe = isPlatformAdminProfile(me);

  if (isCustomerAccessStatus(me.accountStatus, CUSTOMER_ACCESS_STATUS.SUSPENDED)) {
    await loadUserInformation(profile);
    return SESSION_ACCOUNT_SUSPENDED_MESSAGE;
  }

  // platformRole platformAdmin doesnt needs customer-code
  if (!isNonePlatform) {
    const { options: customerRoleOptions, labelMap: customerRoleLabelMap } =
      await fetchCustomerRolesData();
    profile.customerRoleOptions = customerRoleOptions;
    profile.customerRoleLabelMap = customerRoleLabelMap;
    setCustomerRoleOptions?.(customerRoleOptions);
    setCustomerRoleLabelMap?.(customerRoleLabelMap);
  }

  let accessBody;
  try {
    accessBody = await fetchCustomerAccessPayload();
  } catch {
    accessBody = null;
  }

  if (!accessBody || typeof accessBody !== "object") {
    if (isPlatformAdminFromMe) {
      setCustomersList?.(null);
      profile.customerAccess = [];
      // Platform admins get platform-scoped permissions independent of any
      // customer. Without this the very first admin (zero customers) lands on
      // the "Access restricted" page because effectiveAccess/accessTo stay empty.
      await syncEffectiveAccessFromMe(profile, me);
      await loadUserInformation(profile);
      const activeCustomers = await fetchAppActiveCustomers();
      setActiveCustomersList?.(activeCustomers);
      profile.activeCustomers = activeCustomers;
      return null;
    }
    return SESSION_CUSTOMER_ACCESS_LOAD_ERROR_MESSAGE;
  }

  const customersRaw = Array.isArray(accessBody?.customers) ? accessBody.customers : [];
  const rows = mapMeCustomersToAccess(customersRaw);

  if (!rows.length) {
    setCustomersList?.(null);
    profile.customerAccess = [];
    if (isPlatformAdminProfile(profile)) {
      // Fetch platform-scoped effectiveAccess even with no customers, so a
      // fresh platform admin lands on the app (and gets the Add Customer nav)
      // instead of the "Access restricted" page.
      await syncEffectiveAccessFromMe(profile, me);
    }
    await loadUserInformation(profile);
    if (isPlatformAdminProfile(profile)) {
      const activeCustomers = await fetchAppActiveCustomers();
      setActiveCustomersList?.(activeCustomers);
      profile.activeCustomers = activeCustomers;
    }
    if (isNonePlatform) {
      return NO_CUSTOMER_ACCESS_ERROR_MESSAGE;
    }
    return null;
  }

  profile.customerAccess = rows;
  setCustomersList?.(rows);
  if (!isPlatformAdminFromMe) {
    applyActiveCustomerSessionWarning(profile, me, rows);
  }

  if (shouldBootstrapActiveCustomerForNone(me)) {
    const bootstrapResult = await bootstrapActiveCustomerFromAccess(
      profile,
      me,
      rows
    );
    if (bootstrapResult.error) {
      await loadUserInformation(profile);
      return bootstrapResult.error;
    }
    me = bootstrapResult.me;
    applyActiveCustomerSessionWarning(profile, me, rows);
  } else if (isPlatformAdminFromMe) {
    applyActiveCustomerSessionWarning(profile, me, rows);
    // root (`/` / `/home`) is platform-scoped: never keep a selected customer.
    if (isPlatformAdminHomePath(window.location.pathname)) {
      resetPlatformAdminCustomerSelection();
      await syncEffectiveAccessFromMe(profile, { activeCustomerId: null });
    } else {
      const storedCustomer =
        resolveStoredCustomerFromAccessList(rows, me) ??
        (hasStoredSelectedCustomer() ? parseStoredSelectedCustomer() : null);
      if (storedCustomer) {
        writeStoredSelectedCustomer(storedCustomer, profile?.userid);
      }
      await syncEffectiveAccessFromMe(profile, {
        activeCustomerId:
          storedCustomer?.id ?? storedCustomer?.customerId ?? null,
      });
    }
  } else if (hasActiveCustomerOnMe(me)) {
    if (hasUsableActiveCustomerAccessOnMe(me, rows)) {
      syncActiveCustomerStorageFromMe(profile, me);
    } else {
      clearActiveCustomerSelection();
    }
    await syncEffectiveAccessWhenAllowed(profile, me, rows);
  } else {
    applyDefaultCustomerSelection(rows, profile, me);
    const stored = parseStoredSelectedCustomer();
    if (stored?.id != null) {
      await syncEffectiveAccessFromMe(profile, {
        activeCustomerId: stored.id,
      });
    } else {
      await syncEffectiveAccessFromMe(profile, me);
    }
  }

  await loadUserInformation(profile);

  if (isPlatformAdminProfile(profile)) {
    const activeCustomers = await fetchAppActiveCustomers();
    setActiveCustomersList?.(activeCustomers);
    profile.activeCustomers = activeCustomers;
  }

  // platformRole None: pass /me activeCustomerCode on customerRoles.
  if (
    isNonePlatform &&
    hasStoredSelectedCustomer() &&
    me?.activeCustomerCode != null &&
    String(me.activeCustomerCode).trim() !== "" &&
    profile?.userid
  ) {
    applyCustomerContextHeaders(profile.userid, me.activeCustomerCode);
    await syncCustomerRoleOptions(
      profile,
      setCustomerRoleOptions,
      setCustomerRoleLabelMap
    );
    await loadUserInformation(profile);
  } else if (hasStoredSelectedCustomer() && isPlatformAdminProfile(profile)) {
    await syncCustomerRoleOptions(
      profile,
      setCustomerRoleOptions,
      setCustomerRoleLabelMap
    );
    await loadUserInformation(profile);
  }

  return null;
}
